import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface AdminRoleContextType {
  isAdmin: boolean;
  loading: boolean;
}

const AdminRoleContext = createContext<AdminRoleContextType | undefined>(undefined);

export const AdminRoleProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) {
        console.log('🔐 [AdminRoleContext] No user, setting admin to false');
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      console.log('🔐 [AdminRoleContext] Checking admin role for user:', user.id);

      try {
        const { data, error } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: 'admin'
        });

        console.log('🔐 [AdminRoleContext] RPC response - data:', data, 'error:', error);

        if (error) {
          console.error('🔐 [AdminRoleContext] Error checking admin role:', error);
          setIsAdmin(false);
        } else {
          const isAdminResult = data || false;
          console.log('🔐 [AdminRoleContext] Final admin status:', isAdminResult);
          setIsAdmin(isAdminResult);
        }
      } catch (error) {
        console.error('🔐 [AdminRoleContext] Catch error checking admin role:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
        console.log('🔐 [AdminRoleContext] Loading complete');
      }
    };

    checkAdminRole();
  }, [user]);

  return (
    <AdminRoleContext.Provider value={{ isAdmin, loading }}>
      {children}
    </AdminRoleContext.Provider>
  );
};

export const useAdminRoleContext = () => {
  const context = useContext(AdminRoleContext);
  if (context === undefined) {
    throw new Error('useAdminRoleContext must be used within an AdminRoleProvider');
  }
  return context;
};